import { useCallback, useEffect, useRef, type RefObject } from 'react';

import {
    bottomShadowClassName,
    headerHeightVariable,
    leftShadowClassName,
    rightShadowClassName,
    topShadowClassName,
} from './scrollShadows';

interface UseScrollShadowsArgs {
    /** The scrolling element whose overflow decides which shadows show. */
    scrollRef: RefObject<HTMLDivElement | null>;
    /** Re-observes the content whenever the rendered row list changes size. */
    rowCount: number;
}

interface UseScrollShadowsResult {
    /** Attach to the row list; its pseudo-elements carry the top and bottom shadows. */
    verticalShadowRef: RefObject<HTMLDivElement | null>;
    /** Attach to the overlay beside the scroller; its pseudo-elements carry the side shadows. */
    horizontalShadowRef: RefObject<HTMLDivElement | null>;
}

/**
 * Shows a shadow on each edge the scroller can still be scrolled towards. Scoped to
 * `VirtualTableScroller`; anything that is not virtualised should use `ScrollBox` instead.
 */
const useScrollShadows = ({
    scrollRef,
    rowCount,
}: UseScrollShadowsArgs): UseScrollShadowsResult => {
    const verticalShadowRef = useRef<HTMLDivElement>(null);
    const horizontalShadowRef = useRef<HTMLDivElement>(null);

    // Toggled through the DOM instead of state so scrolling never re-renders the rows.
    const updateScrollShadows = useCallback((): void => {
        const scroller = scrollRef.current;

        if (!scroller) return;

        const {
            scrollTop,
            scrollLeft,
            scrollHeight,
            scrollWidth,
            clientHeight,
            clientWidth,
        } = scroller;

        verticalShadowRef.current?.classList.toggle(
            topShadowClassName,
            scrollTop > 0
        );
        verticalShadowRef.current?.classList.toggle(
            bottomShadowClassName,
            Math.ceil(scrollTop + clientHeight) < scrollHeight
        );
        horizontalShadowRef.current?.classList.toggle(
            leftShadowClassName,
            scrollLeft > 0
        );
        horizontalShadowRef.current?.classList.toggle(
            rightShadowClassName,
            Math.ceil(scrollLeft + clientWidth) < scrollWidth
        );

        // Offsets the top strip so its shadow falls onto the rows rather than onto the header.
        // Measured rather than read from clientHeight so the header's bottom border counts too.
        const headerHeight =
            scroller.firstElementChild?.getBoundingClientRect().height ?? 0;

        scroller.style.setProperty(headerHeightVariable, `${headerHeight}px`);
    }, [scrollRef]);

    useEffect(() => {
        const scroller = scrollRef.current;

        if (!scroller) return;

        scroller.addEventListener('scroll', updateScrollShadows, {
            passive: true,
        });

        // Filtering or expanding changes how much is left to scroll, which scrolling never reports.
        const observer = new ResizeObserver(updateScrollShadows);

        observer.observe(scroller);
        for (const child of Array.from(scroller.children))
            observer.observe(child);

        return (): void => {
            scroller.removeEventListener('scroll', updateScrollShadows);
            observer.disconnect();
        };
    }, [scrollRef, updateScrollShadows, rowCount]);

    return { verticalShadowRef, horizontalShadowRef };
};

export { useScrollShadows };
